import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { LogIn, UserPlus, Lock } from "lucide-react";

/**
 * Reusable "you must sign in" gate. Drop this in any flow where an anonymous user is trying
 * to access a download/checkout/registration. Builds proper sign-in + sign-up links with
 * ?next=<current path> so the user lands back where they were after auth.
 *
 * Props:
 *   title    — H3 heading (default: "Sign in to continue")
 *   subtitle — descriptive sub-line
 *   testid   — data-testid for the wrapper
 */
export function SignInRequiredGate({ title, subtitle, testid = "sign-in-gate" }) {
  const { pathname, search } = useLocation();
  const next = encodeURIComponent(`${pathname}${search || ""}`);
  return (
    <div
      className="border border-[#FF331F]/40 bg-[#0A0A0A] p-6 sm:p-8 rounded-sm space-y-5"
      data-testid={testid}
    >
      <div className="flex items-start gap-3">
        <div className="h-10 w-10 grid place-items-center bg-[#FF331F]/15 border border-[#FF331F]/40 rounded-sm shrink-0">
          <Lock className="h-4 w-4 text-[#FF331F]" />
        </div>
        <div>
          <p className="text-[10px] uppercase tracking-[0.3em] text-[#FF331F] font-bold mb-1">Account required</p>
          <h3 className="font-display text-2xl text-white uppercase tracking-tight leading-tight mb-1">
            {title || "Sign in to continue"}
          </h3>
          <p className="text-sm text-zinc-400 leading-relaxed">
            {subtitle || "Everyone who uses a Better Through Boxing service has a free account. Sign in or create one to continue — it takes 30 seconds."}
          </p>
        </div>
      </div>

      <div className="grid sm:grid-cols-2 gap-2">
        <Link to={`/login?next=${next}`} data-testid={`${testid}-login`}>
          <Button className="w-full bg-[#FF331F] hover:bg-[#FF5242] text-white font-bold uppercase tracking-[0.2em] text-xs py-5 rounded-sm">
            <LogIn className="h-3.5 w-3.5 mr-2" /> Sign in
          </Button>
        </Link>
        <Link to={`/register?next=${next}`} data-testid={`${testid}-register`}>
          <Button
            variant="outline"
            className="w-full border-[#27272A] bg-[#050505] text-zinc-200 hover:text-white hover:bg-[#1a1a1a] font-bold uppercase tracking-[0.2em] text-xs py-5 rounded-sm"
          >
            <UserPlus className="h-3.5 w-3.5 mr-2" /> Create account
          </Button>
        </Link>
      </div>
    </div>
  );
}
